import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axiosInstance';

const AddQuestions = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check if it's a CSV file
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please upload a CSV file');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError('');
      setUploadResult(null);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    const formData = new FormData();
    formData.append('questionFile', file);

    setUploading(true);
    setError('');
    setUploadResult(null);

    try {
      const response = await api.post('/api/questions/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadResult(response.data);
      setFile(null);
      // Reset file input
      document.getElementById('file-input').value = '';
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.response?.data?.message || 'Error uploading file');
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleCSV = () => {
    const csvContent = `questionId,questionText,experienceRange,category,topic,qualification,difficultyLevel,optionA,optionB,optionC,optionD,correctOption,explanation,marks
SQL_001,Which SQL clause is used to filter rows?,0-1,technical,sql,"BTech,BCA,BSc",beginner,WHERE,HAVING,GROUP BY,ORDER BY,A,WHERE filters records before aggregation,1
SQL_002,Which command removes all rows without logging each deletion?,01-Feb,technical,sql,"BTech,BCA,BSc",intermediate,DELETE,DROP,TRUNCATE,REMOVE,C,TRUNCATE is faster and minimally logged,1
PY_001,What is the output of: print(type([])),0-1,technical,python,"BTech,BCA,BSc",beginner,<class 'tuple'>,<class 'list'>,<class 'set'>,<class 'dict'>,B,Square brackets create a list,1`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_questions.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Upload Questions</h1>
        <p className="text-muted-foreground mt-2">
          Upload a CSV file containing questions to import them into the database.
        </p>
      </div>

      <div className="space-y-6">
        {/* Upload Form */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h2 className="text-xl font-semibold mb-4">Upload Question File</h2>
          
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Select CSV File *
              </label>
              <input
                id="file-input"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                disabled={uploading}
              />
              {file && (
                <p className="text-sm text-green-600 mt-2">
                  Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
                </p>
              )}
              {error && (
                <p className="text-sm text-red-600 mt-2">{error}</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={!file || uploading}
                className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {uploading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground border-t-transparent"></div>
                    Uploading...
                  </div>
                ) : (
                  'Upload Questions'
                )}
              </button>

              <button
                type="button"
                onClick={downloadSampleCSV}
                className="bg-secondary text-secondary-foreground px-4 py-2 rounded-md hover:bg-secondary/80 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Icon name="Download" size={16} />
                  Download Sample CSV
                </div>
              </button>
            </div>
          </form>
        </div>

        {/* Upload Result */}
        {uploadResult && (
          <div className="bg-card rounded-lg border border-border p-6">
            <h2 className="text-xl font-semibold mb-4">Upload Result</h2>
            <div className="space-y-2">
              <p className="text-green-600">
                ✅ {uploadResult.message}
              </p>
              {uploadResult.importedCount && (
                <p className="text-sm">
                  Imported <strong>{uploadResult.importedCount}</strong> questions
                </p>
              )}
              {uploadResult.skippedCount && (
                <p className="text-sm text-yellow-600">
                  Skipped <strong>{uploadResult.skippedCount}</strong> questions
                </p>
              )}
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-red-600 mb-2">Errors:</p>
                  <ul className="text-sm text-red-600 space-y-1">
                    {uploadResult.errors.slice(0, 5).map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                    {uploadResult.errors.length > 5 && (
                      <li>• ... and {uploadResult.errors.length - 5} more errors</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h2 className="text-xl font-semibold mb-4">Instructions</h2>
          <div className="space-y-3 text-sm">
            <p>
              <strong>File Format:</strong> CSV file with the following columns:
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li><code>questionId</code> - Unique identifier for the question</li>
              <li><code>questionText</code> - The question text</li>
              <li><code>experienceRange</code> - Experience range (e.g., "0-1", "01-Feb", "2+")</li>
              <li><code>category</code> - Category ("aptitude" or "technical")</li>
              <li><code>topic</code> - Topic (e.g., "sql", "python")</li>
              <li><code>qualification</code> - Qualifications (comma-separated, e.g., "BTech,BCA,BSc")</li>
              <li><code>difficultyLevel</code> - Difficulty ("beginner", "intermediate", "advanced")</li>
              <li><code>optionA, optionB, optionC, optionD</code> - Multiple choice options</li>
              <li><code>correctOption</code> - Correct answer ("A", "B", "C", or "D")</li>
              <li><code>explanation</code> - Explanation for the answer</li>
              <li><code>marks</code> - Marks for the question (number)</li>
            </ul>
            <p className="mt-4">
              <strong>Note:</strong> The first row should contain the column headers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddQuestions;
